import { ApolloError } from "@apollo/client";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Alert,
  Button,
  Checkbox,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { FACILITY_INFORMATION_SECTION_ID } from "encamp-shared/src/hmbp/sections";
import { ActivityStatus, ActivityType, SectionSelectionMode } from "generated-graphql/graphql";
import { useUpdateActivity } from "hooks/activities";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useState } from "react";

import { ActivityButtonProps } from ".";
import { useReportDetails } from "../../useReportDetails";
import { SectionSelector } from "../SectionSelector";

// Local mode state mirrors the GraphQL `SectionSelectionMode` enum. `Default`
// means "no `sectionSelectionMode` is sent — server uses Phase B defaults";
// `Manual` sends explicit `sectionIds`; `AutoDetect` triggers Phase C diff.
type Mode = SectionSelectionMode;

const isMode = (value: string): value is Mode =>
  value === SectionSelectionMode.Default ||
  value === SectionSelectionMode.Manual ||
  value === SectionSelectionMode.AutoDetect;

/**
 * Generic fallback for any error we don't have an explicit user-facing
 * mapping for. Server messages can include internal stack frames or raw DB
 * error strings; staff sees this banner and we'd rather show a friendly,
 * actionable line than leak that detail. Known codes get mapped above.
 */
const GENERIC_REQUEUE_FAILURE_MESSAGE =
  "Failed to requeue activity. Please retry or contact support.";

/**
 * Maps a thrown requeue error to a staff-facing message. Looks for typed
 * GraphQL error codes (currently just `NO_SECTION_DRIFT`); anything else
 * falls through to the generic message rather than echoing the server's
 * internal string into the UI. Adding a new known code is a one-line
 * extension to the switch below.
 */
const messageForRequeueError = (e: unknown): string => {
  if (e instanceof ApolloError) {
    const code = e.graphQLErrors?.find((err) => err.extensions?.code)?.extensions?.code;
    switch (code) {
      case "NO_SECTION_DRIFT":
        return "No new changes detected — there's nothing to submit.";
      default:
        return GENERIC_REQUEUE_FAILURE_MESSAGE;
    }
  }
  return GENERIC_REQUEUE_FAILURE_MESSAGE;
};

export const Requeue = ({
  activity,
  canRequeue = true,
  showWarningPanel = false,
  context = "report",
  refetchQueries,
}: ActivityButtonProps & {
  // When false, the Requeue button is hidden but any open dialog stays
  // mounted. The button is gated here (not at the parent's conditional
  // render) so an in-flight submission whose `optimisticResponse` transiently
  // flips activity status to `Queued` doesn't unmount the dialog mid-flight,
  // which would discard `submitError` state and prevent server refusals
  // (e.g. `NO_SECTION_DRIFT`) from surfacing inline.
  canRequeue?: boolean;
  showWarningPanel?: boolean;
  context?: "report" | "cohort";
  refetchQueries?: string[];
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sectionIds, setSectionIds] = useState<string[] | undefined>(undefined);
  const [mode, setMode] = useState<Mode>(SectionSelectionMode.Default);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data } = useReportDetails();
  const [updateActivity, { loading }] = useUpdateActivity(refetchQueries);
  const [createComment] = useCreateTierIIReportComment();

  // Get error message from activity logs
  const { data: logData } = useActivityLogs(activity.id, `status:${ActivityStatus.Error}`, 0, 1, [
    { field: "time", sort: "desc" },
  ]);

  const errorMessage = logData?.activityLogs.items?.[0]?.message ?? null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;
  const facilityState = data?.tierIIReport.facility.state;

  // Section selection only applies to report-context Geppetto requeues for CA
  // (CERS HMBP). Cohort-context requeues never carry section IDs.
  const isCaGeppetto =
    context === "report" && activity.type === ActivityType.Geppetto && facilityState === "CA";

  const contextMessage = context === "cohort" ? "cohort activities page" : "report details page";

  const requeueAndUpdateCache = async (
    sectionIdsForRequeue?: string[],
    selectionMode: Mode = SectionSelectionMode.Default
  ) => {
    const result = await updateActivity({
      variables: {
        id: activity.id,
        input: {
          status: ActivityStatus.Queued,
          message: `Requeued activity from ${contextMessage}.`,
          ...(selectionMode === SectionSelectionMode.Manual
            ? { sectionIds: sectionIdsForRequeue, sectionSelectionMode: selectionMode }
            : {}),
          ...(selectionMode === SectionSelectionMode.AutoDetect
            ? { sectionSelectionMode: selectionMode }
            : {}),
        },
      },
      // Surface server errors on `result.errors` instead of letting the
      // hook's `onError` callback consume them. Without this, Apollo
      // resolves the mutation Promise with `{ data: null, errors: [] }` on
      // failure, the dialog's `await` below doesn't throw, and execution
      // falls through to `createComment` — producing a misleading "Comment
      // added successfully" toast and closing the dialog as if the requeue
      // succeeded.
      errorPolicy: "all",
      update(cache, { data }) {
        if (data?.updateActivity.status) {
          cache.modify({
            id: cache.identify({
              __typename: "Activity",
              id: activity.id,
            }),
            fields: {
              status() {
                return data?.updateActivity?.status;
              },
            },
          });
        }
      },
    });

    // With `errorPolicy: "all"` above, Apollo resolves the mutation Promise
    // with `result.errors` populated instead of rejecting. Surface those
    // errors as a thrown `ApolloError` so `handleDialogSubmit`'s catch fires
    // — otherwise execution falls through to `createComment` below.
    if (result.errors && result.errors.length > 0) {
      throw new ApolloError({ graphQLErrors: [...result.errors] });
    }

    // Add workflow comment with error message
    if (workflowId && reportId && errorMessage) {
      const commentText = `Requeued:\n"${errorMessage}"`;
      await createComment({
        variables: {
          input: {
            reportId,
            workflowId,
            comment: commentText,
          },
        },
      });
    }
  };

  const onClick = () => {
    if (showWarningPanel || isCaGeppetto) {
      setIsDialogOpen(true);
    } else {
      requeueAndUpdateCache();
    }
  };

  const handleModeChange = (next: Mode) => {
    setMode(next);
    if (next === SectionSelectionMode.Manual) {
      setSectionIds([FACILITY_INFORMATION_SECTION_ID]);
    } else {
      setSectionIds(undefined);
    }
  };

  const handleDialogSubmit = async () => {
    setSubmitError(null);
    try {
      await requeueAndUpdateCache(sectionIds, mode);
      setSectionIds(undefined);
      setMode(SectionSelectionMode.Default);
      setIsDialogOpen(false);
    } catch (e) {
      // Keep the dialog open and surface the failure inline so users see
      // server-side refusals (e.g. Phase C empty-drift) without losing context.
      setSubmitError(messageForRequeueError(e));
    }
  };

  const handleDialogCancel = () => {
    setSectionIds(undefined);
    setMode(SectionSelectionMode.Default);
    setSubmitError(null);
    setIsDialogOpen(false);
  };

  return (
    <>
      {canRequeue && (
        <Button
          variant="text"
          size="small"
          color="primary"
          loadingPosition="start"
          onClick={onClick}
          startIcon={<PlayArrowIcon />}
          sx={{ minWidth: 100 }}
        >
          Requeue
        </Button>
      )}
      {isDialogOpen && (
        <RequeueDialog
          activityId={activity.id}
          isOpen={isDialogOpen}
          onCancel={handleDialogCancel}
          loading={loading}
          context={context}
          showWarningPanel={showWarningPanel}
          showSectionSelector={isCaGeppetto}
          sectionIds={sectionIds}
          onSectionIdsChange={setSectionIds}
          mode={mode}
          onModeChange={handleModeChange}
          submitError={submitError}
          onSubmit={handleDialogSubmit}
        />
      )}
    </>
  );
};

interface Props {
  activityId: string;
  onSubmit: () => void;
  onCancel: () => void;
  isOpen: boolean;
  loading: boolean;
  context: "report" | "cohort";
  showWarningPanel: boolean;
  showSectionSelector: boolean;
  sectionIds: string[] | undefined;
  onSectionIdsChange: (next: string[] | undefined) => void;
  mode: Mode;
  onModeChange: (next: Mode) => void;
  submitError: string | null;
}

const RequeueDialog = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  context,
  showWarningPanel,
  showSectionSelector,
  sectionIds,
  onSectionIdsChange,
  mode,
  onModeChange,
  submitError,
}: Props) => {
  const theme = useTheme();
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleRequeueButtonClick = () => {
    onSubmit();
  };

  const handleClose = () => {
    setIsConfirmed(false);
    onCancel();
  };

  const subheaderText = showWarningPanel
    ? context === "cohort"
      ? "Making changes now may affect the cohort workflow, which has already started. Some steps cannot be recalled."
      : "Making changes now may affect the report submission, which has already started. Some steps cannot be recalled, such as submitting a payment."
    : undefined;

  const detailsLinkText = context === "cohort" ? "cohort details" : "report details";

  const requeueDisabled = showWarningPanel ? !isConfirmed : false;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Requeue Activity"
      primaryActionButtonProps={{
        onClick: handleRequeueButtonClick,
        loading,
        disabled: requeueDisabled,
        saveText: "Requeue",
        startIcon: null,
      }}
      subheader={subheaderText}
      description={
        showWarningPanel ? (
          // The shared dialog wrapper already renders `description` inside a
          // <p>; render this as a span to avoid <p> nested in <p>.
          <DialogContentText component="span">
            Submit a ticket to{" "}
            <a
              href="https://encamp.slack.com/archives/C02EUTUBC1H"
              style={{ textDecoration: "none" }}
            >
              #engineering-support
            </a>{" "}
            for manual review by an engineer with these{" "}
            <a href={window.location.href} style={{ textDecoration: "none" }}>
              {detailsLinkText}
            </a>
            .
          </DialogContentText>
        ) : undefined
      }
    >
      {showWarningPanel && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              name="confirmed"
              color="primary"
            />
          }
          label="I've reviewed this with an engineer and want to override the safety check"
        />
      )}
      {showSectionSelector && (
        <FormControl sx={{ mt: theme.spacing(1) }}>
          <FormLabel id="cers-section-selection-label">CERS section selection</FormLabel>
          <RadioGroup
            row
            aria-labelledby="cers-section-selection-label"
            name="cers-section-selection"
            value={mode}
            onChange={(_, v) => {
              if (isMode(v)) {
                onModeChange(v);
              }
            }}
          >
            <FormControlLabel
              value={SectionSelectionMode.Default}
              control={<Radio />}
              label="Defaults"
            />
            <FormControlLabel
              value={SectionSelectionMode.Manual}
              control={<Radio />}
              label="Manual"
            />
            <FormControlLabel
              value={SectionSelectionMode.AutoDetect}
              control={<Radio />}
              label="Auto-detect"
            />
          </RadioGroup>
          {mode === SectionSelectionMode.Manual && (
            <SectionSelector value={sectionIds ?? []} onChange={onSectionIdsChange} />
          )}
        </FormControl>
      )}
      {submitError && <Alert severity="error">{submitError}</Alert>}
    </Dialog>
  );
};
